// Servicio central para todas las operaciones con json-server
import apiService from './api.service'
import { API_ROUTES } from './api/routesMap'

class DatabaseService {
  // ========== USUARIOS ==========
  async getUsers() {
    try {
      return await apiService.get('/usuarios')
    } catch (error) {
      console.error('Error fetching users:', error)
      return []
    }
  }

  async getUserById(id) {
    try {
      return await apiService.get(`/usuarios/${id}`)
    } catch (error) {
      console.error('Error fetching user:', error)
      return null
    }
  }

  async createUser(userData) {
    try {
      // Transformar datos del frontend al formato del backend
      const backendData = {
        // Mapear campos del perfil
        nombres: userData.profile?.name,
        correo: userData.profile?.email,
        dni: userData.profile?.dni,
        telefono: userData.profile?.phone,
        direccion: userData.profile?.address,
        fecha_nacimiento: userData.profile?.birthDate,
        
        // Mapear id_rol basado en el role
        id_rol: this.mapRoleToId(userData.role),
        
        // Campos opcionales
        contrasena: userData.password,
        id_plan: userData.plan?.id,
        activo: userData.active !== undefined ? userData.active : true,
        
        // No incluir username ya que el backend lo genera automáticamente
      }

      return await apiService.post('/usuarios', backendData)
    } catch (error) {
      console.error('Error creating user:', error)
      throw error
    }
  }

  // Mapear roles del frontend a IDs del backend
  mapRoleToId(role) {
    const roleMapping = {
      'ADMIN': 1,
      'ADMINISTRADOR': 1,
      'EXTERNAL_ADMIN': 2,
      'FAMILIAR': 3,
      'CORPORATIVO': 4,
      'EXTERNO': 5
    }
    return roleMapping[role?.toUpperCase()] || 3 // Default: familiar
  }

  async updateUser(id, userData) {
    try {
      return await apiService.put(`/usuarios/${id}`, userData)
    } catch (error) {
      console.error('Error updating user:', error)
      throw error
    }
  }

  async deleteUser(id) {
    try {
      return await apiService.delete(`/usuarios/${id}`)
    } catch (error) {
      console.error('Error deleting user:', error)
      throw error
    }
  }

  // ========== EMERGENCIAS ==========
  async getEmergencies() {
    try {
      return await apiService.get('/emergencias')
    } catch (error) {
      console.error('Error fetching emergencies:', error)
      return []
    }
  }

  async getEmergencyById(id) {
    try {
      return await apiService.get(`/emergencias/${id}`)
    } catch (error) {
      console.error('Error fetching emergency:', error)
      return null
    }
  }

  async createEmergency(emergencyData) {
    try {
      console.log('📤 DATABASE SERVICE - createEmergency called with:', emergencyData)

      // Convertir datos del frontend al formato del backend
      const backendData = {
        codigo: `EMG${Date.now()}`,
        tipo_servicio: emergencyData.type || 'EMERGENCIA',
        id_tipo_servicio_medico: 1, // ID para emergencias
        descripcion: emergencyData.description || 'Solicitud de emergencia',
        sintomas: emergencyData.symptoms || null,
        direccion: emergencyData.location?.address || 'Ubicación GPS',
        coordenadas_lat: emergencyData.location?.coordinates?.latitude || null,
        coordenadas_lng: emergencyData.location?.coordinates?.longitude || null,
        distancia: emergencyData.location?.coordinates?.accuracy ? `${Math.round(emergencyData.location.coordinates.accuracy)}m` : null,
        nombre_paciente: emergencyData.affiliateInfo?.name || 'No especificado',
        edad_paciente: emergencyData.affiliateInfo?.age || null,
        telefono_paciente: emergencyData.affiliateInfo?.phone || null,
        id_afiliado: emergencyData.affiliateInfo?.id !== 'titular' ? emergencyData.affiliateInfo?.id : null,
        relacion_afiliado: emergencyData.affiliateInfo?.relation || 'Titular',
        prioridad: emergencyData.isCritical ? 'critica' : 'media',
        // NUEVOS CAMPOS AGREGADOS:
        isUrgent: emergencyData.isCritical || false,
        extraContacts: emergencyData.extraContacts || [],
        contactos_emergencia: emergencyData.extraContacts || []
      }

      console.log('📤 DATABASE SERVICE - Sending to backend:', backendData)

      return await apiService.post('/emergencias', backendData)
    } catch (error) {
      console.error('Error creating emergency:', error)
      throw error
    }
  }

  async updateEmergency(id, emergencyData) {
    try {
      return await apiService.put(`/emergencias/${id}`, emergencyData)
    } catch (error) {
      console.error('Error updating emergency:', error)
      throw error
    }
  }

  // ========== ENCUESTAS ==========
  async createSurveyResponse(surveyData) {
    try {
      return await apiService.post('/respuestas_encuestas', surveyData)
    } catch (error) {
      console.error('Error creating survey response:', error)
      throw error
    }
  }

  async deleteEmergency(id) {
    try {
      return await apiService.delete(`/emergencias/${id}`)
    } catch (error) {
      console.error('Error deleting emergency:', error)
      throw error
    }
  }

  // ========== EMERGENCIAS PENDIENTES ==========
  async getPendingEmergencies() {
    try {
      return await apiService.get('/emergencias/pendientes')
    } catch (error) {
      console.error('Error fetching pending emergencies:', error)
      return []
    }
  }

  async createPendingEmergency(emergencyData) {
    try {
      const data = {
        ...emergencyData,
        id: emergencyData.id || `PEMG-${Date.now()}`,
        timestamp: new Date().toISOString(),
        status: 'pending'
      }
      return await apiService.post('/emergencias', data)
    } catch (error) {
      console.error('Error creating pending emergency:', error)
      throw error
    }
  }

  async updatePendingEmergency(id, emergencyData) {
    try {
      return await apiService.put(`/emergencias/${id}`, emergencyData)
    } catch (error) {
      console.error('Error updating pending emergency:', error)
      throw error
    }
  }

  async deletePendingEmergency(id) {
    try {
      return await apiService.delete(`/emergencias/${id}`)
    } catch (error) {
      console.error('Error deleting pending emergency:', error)
      throw error
    }
  }

  // ========== UNIDADES MEDICAS ==========
  async getMedicalUnits() {
    try {
      return await apiService.get('/unidades_medicas')
    } catch (error) {
      console.error('Error fetching medicalUnits:', error)
      return []
    }
  }

  async getMedicalUnitById(id) {
    try {
      return await apiService.get(`/unidades_medicas/${id}`)
    } catch (error) {
      console.error('Error fetching medicalUnit:', error)
      return null
    }
  }

  async createMedicalUnit(medicalUnitData) {
    try {
      const data = {
        ...medicalUnitData,
        id: medicalUnitData.id || `AMB-${Date.now()}`,
        createdAt: new Date().toISOString()
      }
      return await apiService.post('/unidades_medicas', data)
    } catch (error) {
      console.error('Error creating medicalUnit:', error)
      throw error
    }
  }

  async updateMedicalUnit(id, medicalUnitData) {
    try {
      return await apiService.put(`/unidades_medicas/${id}`, medicalUnitData)
    } catch (error) {
      console.error('Error updating medicalUnit:', error)
      throw error
    }
  }

  async deleteMedicalUnit(id) {
    try {
      return await apiService.delete(`/unidades_medicas/${id}`)
    } catch (error) {
      console.error('Error deleting medicalUnit:', error)
      throw error
    }
  }

  // ========== ENCUESTAS ==========
  async getSurveyResponses() {
    try {
      return await apiService.get('/respuestas_encuestas')
    } catch (error) {
      console.error('Error fetching survey responses:', error)
      return []
    }
  }

  async createSurveyResponse(surveyData) {
    try {
      const data = {
        ...surveyData,
        id: surveyData.id || Date.now(),
        date: new Date().toISOString(),
        completed: true
      }
      return await apiService.post('/respuestas_encuestas', data)
    } catch (error) {
      console.error('Error creating survey response:', error)
      throw error
    }
  }

  async getSurveyQuestions() {
    try {
      return await apiService.get('/preguntas_encuestas')
    } catch (error) {
      console.error('Error fetching survey questions:', error)
      return []
    }
  }

  // ========== NOTIFICACIONES ==========
  async getNotifications() {
    try {
      return await apiService.get('/notificaciones')
    } catch (error) {
      console.error('Error fetching notifications:', error)
      return []
    }
  }

  async createNotification(notificationData) {
    try {
      const data = {
        ...notificationData,
        id: notificationData.id || Date.now(),
        createdAt: new Date().toISOString(),
        read: false
      }
      return await apiService.post('/notificaciones', data)
    } catch (error) {
      console.error('Error creating notification:', error)
      throw error
    }
  }

  async markNotificationAsRead(id) {
    try {
      const notification = await this.getNotificationById(id)
      if (notification) {
        return await apiService.put(`/notificaciones/${id}`, {
          ...notification,
          read: true,
          readAt: new Date().toISOString()
        })
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw error
    }
  }

  async getNotificationById(id) {
    try {
      return await apiService.get(`/notificaciones/${id}`)
    } catch (error) {
      console.error('Error fetching notification:', error)
      return null
    }
  }

  // ========== AFILIADOS ==========
  async getAffiliates() {
    try {
      return await apiService.get(API_ROUTES.AFFILIATES)
    } catch (error) {
      console.error('Error fetching affiliates:', error)
      return []
    }
  }

  async createAffiliate(affiliateData) {
    try {
      const data = {
        ...affiliateData,
        id: affiliateData.id || Date.now(),
        joinDate: new Date().toISOString(),
        status: 'active'
      }
      return await apiService.post(API_ROUTES.AFFILIATES, data)
    } catch (error) {
      console.error('Error creating affiliate:', error)
      throw error
    }
  }

  async updateAffiliate(id, affiliateData) {
    try {
      return await apiService.put(`/afiliados/${id}`, affiliateData)
    } catch (error) {
      console.error('Error updating affiliate:', error)
      throw error
    }
  }

  async deleteAffiliate(id) {
    try {
      return await apiService.delete(`/afiliados/${id}`)
    } catch (error) {
      console.error('Error deleting affiliate:', error)
      throw error
    }
  }

  // ========== CONTRATOS ==========
  async getContracts() {
    try {
      return await apiService.get('/contratos')
    } catch (error) {
      console.error('Error fetching contracts:', error)
      return []
    }
  }

  async createContract(contractData) {
    try {
      const data = {
        ...contractData,
        id: contractData.id || Date.now(),
        createdAt: new Date().toISOString(),
        status: 'active'
      }
      return await apiService.post('/contratos', data)
    } catch (error) {
      console.error('Error creating contract:', error)
      throw error
    }
  }

  async updateContract(id, contractData) {
    try {
      return await apiService.put(`/contratos/${id}`, contractData)
    } catch (error) {
      console.error('Error updating contract:', error)
      throw error
    }
  }

  async deleteContract(id) {
    try {
      return await apiService.delete(`/contratos/${id}`)
    } catch (error) {
      console.error('Error deleting contract:', error)
      throw error
    }
  }

  // ========== PLANES ==========
  async getPlans() {
    try {
      return await apiService.get(API_ROUTES.PLANS)
    } catch (error) {
      console.error('Error fetching plans:', error)
      return []
    }
  }

  async getPlanConfiguration() {
    try {
      return await apiService.get(API_ROUTES.PLAN_CONFIGURATION)
    } catch (error) {
      console.error('Error fetching plan configuration:', error)
      return {}
    }
  }

  async getAdminPlanConfiguration() {
    try {
      return await apiService.get(API_ROUTES.ADMIN_PLAN_CONFIGURATION)
    } catch (error) {
      console.error('Error fetching admin plan configuration:', error)
      return {}
    }
  }

  async updateAdminPlanConfiguration(configData) {
    try {
      return await apiService.put(API_ROUTES.ADMIN_PLAN_CONFIGURATION, configData)
    } catch (error) {
      console.error('Error updating admin plan configuration:', error)
      throw error
    }
  }

  // ========== ESTADÍSTICAS ==========
  async getStats() {
    try {
      const response = await apiService.get('/estadisticas_dashboard')
      return response.dashboard || response || {}
    } catch (error) {
      console.error('Error fetching stats:', error)
      return {
        totalEmergencies: 0,
        activeEmergencies: 0,
        availableMedicalUnits: 0,
        totalMedicalUnits: 0,
        monthlyRevenue: 0,
        totalUsers: 0,
        averageResponseTime: '0 min',
        satisfactionRate: 0,
        totalAffiliates: 0,
        activeContracts: 0
      }
    }
  }

  async updateStats(statsData) {
    try {
      return await apiService.put('/estadisticas', statsData)
    } catch (error) {
      console.error('Error updating stats:', error)
      throw error
    }
  }

  // ========== ENTIDADES EXTERNAS ==========
  async getExternalEntities() {
    try {
      return await apiService.get('/entidades_externas')
    } catch (error) {
      console.error('Error fetching external entities:', error)
      return []
    }
  }

  async createExternalEntity(entityData) {
    try {
      const data = {
        ...entityData,
        id: entityData.id || `EXT-${Date.now()}`,
        createdAt: new Date().toISOString()
      }
      return await apiService.post('/entidades_externas', data)
    } catch (error) {
      console.error('Error creating external entity:', error)
      throw error
    }
  }

  async updateExternalEntity(id, entityData) {
    try {
      return await apiService.put(`/entidades_externas/${id}`, entityData)
    } catch (error) {
      console.error('Error updating external entity:', error)
      throw error
    }
  }

  async deleteExternalEntity(id) {
    try {
      return await apiService.delete(`/entidades_externas/${id}`)
    } catch (error) {
      console.error('Error deleting external entity:', error)
      throw error
    }
  }

  // ========== CONFIGURACIÓN DE PAGOS ==========
  async getPaymentConfig() {
    try {
      return await apiService.get('/configuracion_pagos')
    } catch (error) {
      console.error('Error fetching payment config:', error)
      return {}
    }
  }

  async updatePaymentConfig(configData) {
    try {
      return await apiService.put('/configuracion_pagos', configData)
    } catch (error) {
      console.error('Error updating payment config:', error)
      throw error
    }
  }

  // ========== HISTORIAL DE SERVICIOS ==========
  async getServiceHistory() {
    try {
      return await apiService.get('/historial_servicios')
    } catch (error) {
      console.error('Error fetching service history:', error)
      return []
    }
  }

  async createServiceHistory(serviceData) {
    try {
      const data = {
        ...serviceData,
        id: serviceData.id || `SERV-${Date.now()}`,
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0],
        status: 'completed'
      }
      return await apiService.post('/historial_servicios', data)
    } catch (error) {
      console.error('Error creating service history:', error)
      throw error
    }
  }

  // ========== TRANSACCIONES (NO EXISTE EN DB.JSON - CREAR) ==========
  async getTransactions() {
    try {
      // Por ahora retornar array vacío hasta que se agregue a db.json
      return []
    } catch (error) {
      console.error('Error fetching transactions:', error)
      return []
    }
  }

  async createTransaction(transactionData) {
    try {
      // Por ahora solo loguear hasta que se agregue a db.json
      console.log('Transaction to create:', transactionData)
      return transactionData
    } catch (error) {
      console.error('Error creating transaction:', error)
      throw error
    }
  }

  // ========== SOLICITUDES DE REGISTRO (NO EXISTE EN DB.JSON - CREAR) ==========
  async getRegistrationRequests() {
    try {
      // Por ahora retornar array vacío hasta que se agregue a db.json
      return []
    } catch (error) {
      console.error('Error fetching registration requests:', error)
      return []
    }
  }

  async createRegistrationRequest(requestData) {
    try {
      // Por ahora solo loguear hasta que se agregue a db.json
      console.log('Registration request to create:', requestData)
      return requestData
    } catch (error) {
      console.error('Error creating registration request:', error)
      throw error
    }
  }

  async updateRegistrationRequest(id, requestData) {
    try {
      // Por ahora solo loguear hasta que se agregue a db.json
      console.log('Registration request to update:', id, requestData)
      return requestData
    } catch (error) {
      console.error('Error updating registration request:', error)
      throw error
    }
  }

  // ========== MÉTODOS DE UTILIDAD ==========
  async loadAllData() {
    try {
      const [
        users,
        emergencies,
        pendingEmergencies,
        medicalUnits,
        surveyResponses,
        notifications,
        affiliates,
        contracts,
        plans,
        stats,
        externalEntities,
        serviceHistory
      ] = await Promise.all([
        this.getUsers(),
        this.getEmergencies(),
        this.getPendingEmergencies(),
        this.getMedicalUnits(),
        this.getSurveyResponses(),
        this.getNotifications(),
        this.getAffiliates(),
        this.getContracts(),
        this.getPlans(),
        this.getStats(),
        this.getExternalEntities(),
        this.getServiceHistory()
      ])

      return {
        users,
        emergencies,
        pendingEmergencies,
        medicalUnits,
        surveyResponses,
        notifications,
        affiliates,
        contracts,
        plans,
        stats,
        externalEntities,
        serviceHistory
      }
    } catch (error) {
      console.error('Error loading all data:', error)
      return {}
    }
  }
}

export default new DatabaseService()